import type { CornerDotType, CornerSquareType, DotType, Gradient, Mode, Options } from "qr-code-styling";
import type { Ecc } from "./capacity";

/** One shape choice drives both the data modules and the corner finder patterns. */
export type ShapeStyle = "square" | "rounded" | "dots" | "classy";
export type ColorStyle = "black" | "blue" | "green" | "red" | "gradient";
export type SolidColorStyle = Exclude<ColorStyle, "gradient">;

export interface QrStyle {
	shape: ShapeStyle;
	color: ColorStyle;
	logo: boolean;
}

export const SHAPE_STYLES: readonly ShapeStyle[] = ["square", "rounded", "dots", "classy"];
export const COLOR_STYLES: readonly ColorStyle[] = ["black", "blue", "green", "red", "gradient"];
export const DEFAULT_STYLE: QrStyle = { shape: "dots", color: "black", logo: false };

export const BRAND_BLUE = "#3f72e0";
export const BRAND_VIOLET = "#730f8c";
/** Dark enough on white for reliable scanning (contrast well above 4.5:1). */
export const BRAND_GREEN = "#1e7b34";
export const BRAND_RED = "#c62828";

export const SOLID_COLORS: Record<SolidColorStyle, string> = {
	black: "#000000",
	blue: BRAND_BLUE,
	green: BRAND_GREEN,
	red: BRAND_RED,
};

/** CSS-style angle of the brand gradient, in degrees (0 = up, 90 = right). */
export const GRADIENT_ANGLE_DEG = 134;

/** A centre logo hides modules, so the strongest error correction is mandatory with it. */
export const LOGO_ECC: Ecc = "H";
/**
 * qr-code-styling scales the logo to hide at most this share of the modules the chosen
 * error-correction level can recover: 0.4 × 30% = 12% of the symbol at level H. This is
 * the library's recommended maximum.
 */
export const LOGO_COVERAGE = 0.4;
/** Quiet zone around the symbol as a fraction of the image size. */
const QUIET_ZONE_RATIO = 0.05;
/** Blank margin between the logo and the surrounding modules, as a fraction of the image size. */
const LOGO_MARGIN_RATIO = 0.015;

export function effectiveEcc(ecc: Ecc, style: QrStyle): Ecc {
	return style.logo ? LOGO_ECC : ecc;
}

/** Mirrors qr-code-styling's automatic mode detection so version info matches what is drawn. */
export function detectMode(text: string): Mode {
	if (/^[0-9]*$/.test(text)) return "Numeric";
	if (/^[0-9A-Z $%*+\-./:]*$/.test(text)) return "Alphanumeric";
	return "Byte";
}

interface ShapeTypes {
	dots: DotType;
	cornerSquare: CornerSquareType;
	cornerDot: CornerDotType;
}

/** Library types for each shape. Corners follow the module shape; "classy" gets rounded corners. */
const SHAPE_TYPES: Record<ShapeStyle, ShapeTypes> = {
	square: { dots: "square", cornerSquare: "square", cornerDot: "square" },
	rounded: { dots: "rounded", cornerSquare: "extra-rounded", cornerDot: "dot" },
	dots: { dots: "dots", cornerSquare: "dot", cornerDot: "dot" },
	classy: { dots: "classy", cornerSquare: "extra-rounded", cornerDot: "dot" },
};

export function brandGradient(): Gradient {
	return {
		type: "linear",
		rotation: (GRADIENT_ANGLE_DEG * Math.PI) / 180,
		colorStops: [
			{ offset: 0, color: BRAND_BLUE },
			{ offset: 1, color: BRAND_VIOLET },
		],
	};
}

function paint(color: ColorStyle): { color?: string; gradient?: Gradient } {
	return color === "gradient" ? { gradient: brandGradient() } : { color: SOLID_COLORS[color] };
}

/** Build the full qr-code-styling options object for one render or export. */
export function buildOptions(text: string, ecc: Ecc, sizePx: number, style: QrStyle, logoUrl: string): Options {
	const fill = paint(style.color);
	const shape = SHAPE_TYPES[style.shape];
	const options: Options = {
		type: "canvas",
		width: sizePx,
		height: sizePx,
		margin: Math.round(sizePx * QUIET_ZONE_RATIO),
		data: text,
		qrOptions: { typeNumber: 0, mode: detectMode(text), errorCorrectionLevel: effectiveEcc(ecc, style) },
		dotsOptions: { type: shape.dots, ...fill },
		cornersSquareOptions: { type: shape.cornerSquare, ...fill },
		cornersDotOptions: { type: shape.cornerDot, ...fill },
		backgroundOptions: { color: "#ffffff" },
	};
	if (style.logo) {
		options.image = logoUrl;
		options.imageOptions = {
			hideBackgroundDots: true,
			imageSize: LOGO_COVERAGE,
			margin: Math.round(sizePx * LOGO_MARGIN_RATIO),
			crossOrigin: "anonymous",
			saveAsBlob: true,
		};
	}
	return options;
}

export function isQrStyle(value: unknown): value is QrStyle {
	if (typeof value !== "object" || value === null) return false;
	const v = value as Record<string, unknown>;
	return (
		SHAPE_STYLES.includes(v.shape as ShapeStyle) &&
		COLOR_STYLES.includes(v.color as ColorStyle) &&
		typeof v.logo === "boolean"
	);
}

/** Parse a stored style, falling back to the default for anything malformed. */
export function parseStyle(json: string | null | undefined): QrStyle {
	if (!json) return { ...DEFAULT_STYLE };
	try {
		const parsed: unknown = JSON.parse(json);
		return isQrStyle(parsed) ? { ...parsed } : { ...DEFAULT_STYLE };
	} catch {
		return { ...DEFAULT_STYLE };
	}
}
